import React, { useState } from 'react'
import { Button, Form, InputGroup, Row, Spinner } from 'react-bootstrap'
import {
  REPORTID_SUBHEADING,
  CONCENT_LBL_1,
  CONCENT_LBL_2,
  INVALID_ID_WARNING,
  DISCLAIMER,
  DOWNLOAD_TEXT,
  PURPOSE_OF_SEARCH_OPTIONS,
  PRECHECK_SUBHEADING,
  NCA_TEXT,
  CMAINFO_DISCLAIMER,
  API_ERROR_MESSAGE,
  PRICELIST,
} from '../../utils/Constants'
import { validateIdNumber } from '../../utils/Validation'
import FormInputWithAlert from '../../components/form/FormInput/FormInputWithAlert'
import TextArea from '../../components/form/TextArea/TextArea'
import ScreenHeader from '../../components/kyc/ScreenHeader'
import FormInput from '../../components/form/FormInput/FormInput'
import FooterButtons from '../../components/form/FooterButtons/FooterButtons'
import Checkbox from '../../components/form/Checkbox/Checkbox'
import { ReportTypes } from '../../api/apiConstants'
import { ReportResponseForm } from '../../components/kyc/ReportResponseForm'
import SearchBySelect from '../../components/form/SearchBySelect/SearchBySelect'
import './Precheck.css'
import CreditReportResultTable from '../../components/kyc/CreditReportResultTable'
import axios, { AxiosResponse } from 'axios'
import { getAPIUrl } from '../../api/API'
import { useMutation } from 'react-query'
import { usePrecheckSearch } from './hooks'
import KycButton from '../../components/form/Button/Button'
import moment from 'moment'
import Notice from '../../components/Notice/Notice'

enum SearchTypes {
  ID = 'id',
  PASSPORT = 'passport',
  PERSONAL = 'personal',
}

import NewAndCloseButtons from '../../components/form/NewAndCloseButtons/NewAndCloseButtons'
import '../../components/form/FormInput/FormInput.css'
import ScreenLayout from '../../components/layouts/ScreenLayout'
import { setuid } from 'process'
import { getFilenameFromHeader } from '../../utils/get-filename-header'
import { useDownloadFile } from '../../hooks/use-download-file'
import { ReactDatePicker } from '../../components/kyc/ReactDatePicker'

interface CreditReportByIdProps {
  handleClose: () => void
  identityNumber: string
  passportNumber: string
  setUIDNumber: (uidNumber: {
    target: { value: React.SetStateAction<string> }
  }) => void
  firstName: string
  surname: string
  creditReportPurpose: string
  setCreditReportPurpose: (value: string) => void
  setSurname: (value: string) => void
  setFirstName: (value: string) => void
  concent1: boolean
  setFirstConcent: (value: boolean) => void
  concent2: boolean
  KYCAccess: boolean
  showOTPFields: boolean
  showDownloadReport: boolean
  handleSearch?: () => void
  setSecondConcent: (value: boolean) => void
  showProgress: boolean
  initialState: { [key: string]: any }
  setInitialState: React.Dispatch<React.SetStateAction<any>>
  setIsError: (value: boolean) => void
  setErrorMessage: (value: string) => void
  setReportType: (value: number) => void
  reference: string
  setReference: (value: string) => void
  handleClick: () => void
  setShow: (value: boolean) => void
  handleStartOver: () => void
}

export type Person = {
  consumerID: string
  idno: string
  passportnumber: string
  firstname: string
  secondname: string
  thirdname: string
  surname: string
  birthDate: string
  gender: string
  enquiryID: number
  enquiryResultID: number
  reference: string
}

const CreditReportById: React.FC<CreditReportByIdProps> = ({
  handleClose,
  identityNumber,
  passportNumber,
  setUIDNumber,
  firstName,
  setFirstName,
  surname,
  setSurname,
  concent1,
  setFirstConcent,
  concent2,
  setSecondConcent,
  showOTPFields,
  KYCAccess,
  showDownloadReport,
  initialState,
  setInitialState,
  setShow,
  setIsError,
  setErrorMessage,
  setReportType,
  reference,
  setReference,
  handleClick,
  creditReportPurpose,
}) => {
  const updateInitialState = () => {
    setInitialState({
      ...initialState,
      creditReportById: {
        identityNumber: identityNumber,
        reference: reference,
      },
    })
  }

  const onSearch = () => {
    const missingFields = validateFields()
    if (missingFields.length > 0)
      return alert(
        `Please fill in the following fields: ${missingFields.join(', ')}`,
      )
    setReportType(ReportTypes.CreditEnquiryReport.reportTypeId)
    updateInitialState()
    searchCreditReportCandidate()
  }

  const [isOtp, setIsOtp] = useState(false)
  const [selected, setSelected] = useState<Person | null>(null)
  const [reportId, setReportId] = useState('')

  const [purposeOfSearch, setPurposeOfSearch] = useState('')

  const [date, setDate] = useState<Date | null>(null)

  const [creditSearchResults, setCreditSearchResults] = useState<Person[]>([])

  const handleStartOver = () => {
    setIsOtp(false)
    setDate(null)
    setFirstName('')
    setSurname('')
    setUIDNumber({ target: { value: '' } })
    setReference('')
    setCreditSearchResults([])
    setPurposeOfSearch('')
  }

  const validateFields = () => {
    const missingFields = []
    if (!(surname !== '' || firstName !== '' || date !== null)) {
      if (!validateIdNumber(identityNumber)) {
        missingFields.push('ID Number')
      }
      if (purposeOfSearch === '') missingFields.push('Prescribed Purpose')
      return missingFields
    }
    if (surname === '') missingFields.push('Surname')
    if (firstName === '') missingFields.push('First Name')
    if (date === null) missingFields.push('Date of Birth')
    if (purposeOfSearch === '') missingFields.push('Prescribed Purpose')
    if (!concent1) missingFields.push('Consent 1')
    if (!concent2) missingFields.push('Consent 2')
    return missingFields
  }

  const { searchCreditReportCandidate, isLoading } = usePrecheckSearch(
    {
      identityNumber: identityNumber,
      passportNumber: passportNumber ?? undefined,
      firstName: firstName ?? undefined,
      lastName: surname ?? undefined,
      dateOfBirth: date ? moment(date).format('YYYY-MM-DD') : null,
      reference,
    },
    (data) => {
      if (data.length === 0) {
        let searchCriteria = ''

        if (identityNumber)
          searchCriteria += `Identity Number: ${identityNumber} `
        if (passportNumber)
          searchCriteria += `Passport Number: ${passportNumber} `
        if (firstName) searchCriteria += `First Name: ${firstName} `
        if (surname) searchCriteria += `Last Name: ${surname} `
        if (date)
          searchCriteria += `Date of Birth: ${moment(date).format(
            'YYYY-MM-DD',
          )} `

        alert(`No results found for: ${searchCriteria}`)
      } else {
        setCreditSearchResults(data)
      }
    },
    (e: any) => {
      setIsError(true)
      console.log(e.response.data.statusCode)
      if (e.response.data.statusCode === 500) {
        return setErrorMessage('No results found! Please try again')
      }
      setErrorMessage('Error searching for candidate')
    },
  )

  const { mutate: downloadReport, isLoading: downloadingReport } =
    useDownloadFile(
      '/PreCheck/download-report',
      reportId,
      ReportTypes.PrecheckEnquiryReport.reportTypeId,
      setIsError,
      setErrorMessage,
    )

  type FetchReportVariables = {
    identityNumber: string
    enquiryID: number
    enquiryResultID: number
    reference: string
    productId: number
    reportType: number
    purpose: string
  }

  const { mutate: fetchReport, isLoading: fetchingReport } = useMutation(
    'fetch-report',
    async (fetchReportVariables: FetchReportVariables) => {
      const res = await axios.post(
        getAPIUrl() + '/PreCheck/load-report',
        fetchReportVariables,
        {
          headers: {
            Authorization: 'Bearer ' + sessionStorage.getItem('jwt'),
          },
        },
      )
      return res.data
    },
    {
      onSuccess: (response: any) => {
        setReportId(response.data.reportId)
        downloadReport(response.data.reportId)
      },
      onError: (error: any) => {
        setIsError(true)
        setErrorMessage(error?.response?.data?.message || API_ERROR_MESSAGE)
      },
    },
  )

  const handleFetchReport = (person: Person) => {
    setSelected(person)
    fetchReport({
      ...person,
      productId: 235,
      reportType: 10,
      identityNumber: person.idno,
      purpose: purposeOfSearch,
    })
  }

  const PRECHECK_TITLE = 'Pre-Check Report'
  const PRECHECK_SAMPLE_REPORT = 'Precheck_report.pdf'
  const subHeader = (
    <span>
      • Confirmation Photo <br />
      • Credit Score <br />• Risk Category
    </span>
  )

  if (creditSearchResults.length > 0)
    return (
      <ScreenLayout
        price={PRICELIST.PRECHECK}
        headerText={PRECHECK_TITLE}
        subHeaderText={subHeader}
        sampleReport={PRECHECK_SAMPLE_REPORT}
      >
        <CreditReportResultTable
          showProgress={fetchingReport || downloadingReport}
          handleLoadCreditReport={handleFetchReport}
          results={creditSearchResults}
        />
        <NewAndCloseButtons
          handleAction={handleStartOver}
          handleClose={setShow}
        />
      </ScreenLayout>
    )

  return (
    <ScreenLayout
      price={PRICELIST.PRECHECK}
      headerText={PRECHECK_TITLE}
      subHeaderText={subHeader}
      sampleReport={PRECHECK_SAMPLE_REPORT}
    >
      {isLoading && (
        <>
          <div className="spinner-container">
            <Spinner
              as="span"
              animation="border"
              variant="dark"
              role="status"
              aria-hidden="true"
            ></Spinner>
            <span> </span>
          </div>
        </>
      )}
      <Form.Group>
        {!KYCAccess && (
          <p className="lbl-red">
            You do not have access to this report, please consult with your
            administrator to give you access.
          </p>
        )}
        <FormInputWithAlert
          className="mb-3"
          value={identityNumber}
          setValue={setUIDNumber}
          placeholder="Unique identity number"
          validationFunction={validateIdNumber}
          validationMessage={INVALID_ID_WARNING}
        />
        <FormInput
          className="mb-3"
          placeHolderText="Surname"
          onChangeHandler={setSurname}
          value={surname}
        />
        <FormInput
          className="mb-3"
          placeHolderText="Name"
          onChangeHandler={setFirstName}
          value={firstName}
        />
        <InputGroup
          className="mb-3"
          style={{ width: '100%', maxWidth: '430px', zIndex: 10 }}
        >
          <ReactDatePicker
            selected={date}
            onChange={(date: Date) => setDate(date)}
            placeholderText="Date of Birth (CCYY-MM-DD)"
          />
        </InputGroup>
        <SearchBySelect
          className="mb-3"
          onChangeHandler={(value: string) => setPurposeOfSearch(value)}
          options={PURPOSE_OF_SEARCH_OPTIONS}
        />
      </Form.Group>

      <FormInput
        className="mb-3"
        placeHolderText="Search reference (Optional)"
        onChangeHandler={setReference}
        value={reference}
      />

      <Checkbox
        className="checkBoxes"
        label={
          <span className="checkBoxLabelText">
            I confirm that consent has been received from the individual(s), of
            which <br />
            evidence must be made available in the event of an audit.{' '}
          </span>
        }
        checked={concent1}
        onChangeHandler={() => {
          setFirstConcent(!concent1)
        }}
        required
      />
      <Checkbox
        className="checkBoxes"
        label={
          <span className="checkBoxLabelText">
            I declare I am accessing this information for it's prescribed or
            contemplated <br />
            purpose only. I will not alter or amend the credit report.{' '}
          </span>
        }
        checked={concent2}
        onChangeHandler={() => {
          setSecondConcent(!concent2)
        }}
        required
      />

      <span className="legalText">{NCA_TEXT}</span>
      <span className="belowLegal">{DOWNLOAD_TEXT}</span>

      {!showOTPFields && !showDownloadReport && (
        <>
          <FooterButtons
            setShow={handleClose}
            isDisabled={!KYCAccess}
            handleSearch={onSearch}
            isLoading={isLoading}
          />

          {/* <Notice /> */}
          <p className="disclaimer">{DISCLAIMER}</p>
          <span className="cmaDisclaimerPR">{CMAINFO_DISCLAIMER}</span>
        </>
      )}

      {showDownloadReport && !showOTPFields && (
        <>
          <KycButton
            // onClick={handleClick}
            variant={'primary'}
            children={'Download Report'}
            isLoading={downloadingReport}
            size="lg"
          />
          <NewAndCloseButtons
            handleAction={handleStartOver}
            handleClose={handleClose}
          />
          <span className="legalText">{NCA_TEXT}</span>
          <span className="cmaDisclaimerPR">{CMAINFO_DISCLAIMER}</span>

          <p className="darkPleaseDownload">
            PLEASE DOWNLOAD AND SAVE A COPY FOR FUTURE ACCESS
          </p>
        </>
      )}
    </ScreenLayout>
  )
}

export default CreditReportById
